import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../context/I18nContext';
import { useCurrency } from '../context/CurrencyContext';
import { useRegion } from '../context/RegionContext';
import { getSessionId } from '../utils/session';
import { getCookieConsent } from './CookieBanner';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

/**
 * Fires /api/analytics/track with a stable session_id so the backend can
 * segment unique browsers. Re-fires whenever lang / region / currency
 * changes so the latest preferences are persisted per session.
 *
 * Respects the visitor's cookie-banner choice: if they explicitly rejected
 * non-essential cookies (or haven't decided yet), we skip the analytics
 * beacon entirely. Once they accept, we re-render via the
 * `zhelta-cookie-consent-changed` event so the next page-view fires.
 */
export default function AnalyticsBeacon() {
  const { lang } = useI18n();
  const { currency } = useCurrency();
  const { region, detected, detecting } = useRegion();
  const lastPayload = useRef('');
  const [consent, setConsent] = useState(() => getCookieConsent());

  // React to the user accepting/rejecting after first paint.
  useEffect(() => {
    const onChange = () => setConsent(getCookieConsent());
    window.addEventListener('zhelta-cookie-consent-changed', onChange);
    window.addEventListener('zhelta-cookie-consent-cleared', onChange);
    return () => {
      window.removeEventListener('zhelta-cookie-consent-changed', onChange);
      window.removeEventListener('zhelta-cookie-consent-cleared', onChange);
    };
  }, []);

  useEffect(() => {
    if (detecting) return;
    // GDPR/CCPA gate — only fire when the visitor opted in. No consent yet
    // (banner still showing) → also skip; the event listener above will
    // re-fire this effect once they accept.
    if (consent?.choice !== 'accepted') return;
    const body = {
      session_id: getSessionId(),
      path: window.location.pathname,
      lang,
      region: region || detected || null,
      currency,
      country_code: detected || region || null,
      country: null,
    };
    const key = JSON.stringify(body);
    if (key === lastPayload.current) return;
    lastPayload.current = key;

    fetch(`${API}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: key,
    }).catch(() => {
      /* swallow: analytics must never break the UI */
    });
  }, [detecting, detected, region, lang, currency, consent]);

  return null;
}
